import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, SectionLabel } from "@/components/Reveal";

const SIDES = [
  { tag: "US", title: "MidHq", sub: "ISO merchant banking", points: ["High-risk underwriting", "Own sponsor banks", "POS, gateway & ACH", "US merchant boarding"], accent: "text-sky-400" },
  { tag: "INTL", title: "FinBPM", sub: "Merchant of Record", points: ["Global checkout", "Tax & invoicing", "Fraud, refunds & chargebacks", "Settlement-grade reporting"], accent: "text-[var(--gold)]" },
];

export function Alliance() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="alliance" ref={ref} className="relative bg-[#0a0f18] py-28 sm:py-40" data-testid="alliance-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-10">
        <Reveal><SectionLabel index="02">The FinBPM Alliance</SectionLabel></Reveal>
        <div className="mt-14 grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="clip-frame relative aspect-[4/5] overflow-hidden">
              <motion.img src="/img/alliance.jpg" alt="Partners meeting across the table" style={{ y: imgY, scale: 1.18 }}
                className="h-full w-full object-cover grayscale-[35%]" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,transparent_30%,rgba(8,11,17,0.75)_90%)]" />
              <div className="absolute bottom-6 left-6 right-6 border border-white/10 bg-[#080B11]/80 p-5 backdrop-blur-md">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--gold)]">International partner</p>
                <p className="mt-2 font-display text-2xl">Milen Marinov</p>
                <p className="text-sm text-slate-300">FinBPM · finbpm.tech</p>
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-6xl">
                Two desks, one corridor. <span className="italic font-light text-[var(--gold)]">Cross-border, handled.</span>
              </h2>
              <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">
                FinBPM runs checkout, tax, risk and settlement internationally as Merchant of Record. MidHq is the US side: high-risk ISO processing backed by our own banks. Merchants enter through either door and get both.
              </p>
            </Reveal>
            <div className="mt-12 grid gap-4 sm:grid-cols-2">
              {SIDES.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.1} className="border border-white/10 bg-[#0d1524] p-7" data-testid={`alliance-card-${s.title.toLowerCase()}`}>
                  <div className="flex items-center justify-between">
                    <span className={`font-mono text-xs ${s.accent}`}>{s.tag}</span>
                    <span className="h-px flex-1 mx-4 bg-white/10" />
                  </div>
                  <h3 className="mt-6 font-display text-3xl font-semibold">{s.title}</h3>
                  <p className="text-sm text-slate-400">{s.sub}</p>
                  <ul className="mt-6 space-y-2 text-sm text-slate-200">
                    {s.points.map((p) => <li key={p} className="flex gap-3"><span className={s.accent}>—</span>{p}</li>)}
                  </ul>
                </Reveal>
              ))}
            </div>
            <Reveal className="mt-10 flex flex-wrap gap-3">
              <Link to="/portal" className="btn-primary" data-testid="alliance-portal-button">Open the Corridor Portal <ArrowUpRight className="h-4 w-4" /></Link>
              <a href="https://www.finbpm.tech" target="_blank" rel="noreferrer" className="btn-ghost" data-testid="alliance-finbpm-link">Visit FinBPM</a>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
